import React, { useState, useCallback } from 'react';
import { 
  KanaChar, 
  getKanaByVowel, 
  vowelOptions, 
  VowelType,
  KanaFilterOptions
} from '../data/kana';
import './HiraganaQuiz.css';

// Shuffle array using Fisher-Yates algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Generate wrong answers only from the available character pool
const generateOptions = (
  correct: KanaChar,
  availableChars: KanaChar[],
  numOptions: number = 4
): string[] => {
  const wrongOptions = availableChars
    .filter(char => char.romaji !== correct.romaji)
    .map(char => char.romaji);
  
  const uniqueWrong = Array.from(new Set(wrongOptions));
  const shuffledWrong = shuffleArray(uniqueWrong).slice(0, numOptions - 1);
  const allOptions = [...shuffledWrong, correct.romaji];
  
  return shuffleArray(allOptions);
};

type GameState = 'menu' | 'quiz';
type KanaMode = 'hiragana' | 'katakana' | 'both';

const HiraganaQuiz: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>('menu');
  const [kanaMode, setKanaMode] = useState<KanaMode>('hiragana');
  const [selectedVowels, setSelectedVowels] = useState<VowelType[]>([...vowelOptions]);
  const [includeDakuten, setIncludeDakuten] = useState(false);
  const [includeHandakuten, setIncludeHandakuten] = useState(false);
  
  // Store the full pool of available characters for generating options
  const [availableChars, setAvailableChars] = useState<KanaChar[]>([]);
  const [questions, setQuestions] = useState<KanaChar[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options, setOptions] = useState<string[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  // Get filter options based on kana mode
  const getFilterOptions = (): KanaFilterOptions => ({
    includeHiragana: kanaMode === 'hiragana' || kanaMode === 'both',
    includeKatakana: kanaMode === 'katakana' || kanaMode === 'both',
    includeDakuten,
    includeHandakuten,
  });

  // Get character count for display
  const getCharCount = () => {
    return getKanaByVowel(selectedVowels, getFilterOptions()).length;
  };

  // Setup a new question using the available character pool
  const setupQuestion = useCallback((chars: KanaChar[], index: number, pool: KanaChar[]) => {
    if (chars.length === 0) return;
    const currentChar = chars[index];
    // Generate options only from the filtered pool
    setOptions(generateOptions(currentChar, pool));
    setSelectedAnswer(null);
    setIsCorrect(null);
  }, []);

  // Start the quiz
  const startQuiz = () => {
    if (selectedVowels.length === 0) {
      alert('Please select at least one vowel row!');
      return;
    }
    
    const filterOptions = getFilterOptions();
    const chars = getKanaByVowel(selectedVowels, filterOptions);
    
    if (chars.length === 0) {
      alert('No characters available with current selection!');
      return;
    }
    
    const shuffled = shuffleArray(chars);
    setAvailableChars(chars);
    setQuestions(shuffled);
    setCurrentIndex(0);
    setScore(0);
    setAnswered(0);
    setStreak(0);
    setBestStreak(0);
    setGameState('quiz');
    setupQuestion(shuffled, 0, chars);
  };

  // Handle answer selection
  const handleAnswer = (answer: string) => {
    if (selectedAnswer !== null) return;
    
    const currentQuestion = questions[currentIndex];
    const correct = answer === currentQuestion.romaji;
    
    setSelectedAnswer(answer);
    setIsCorrect(correct);
    setAnswered(prev => prev + 1);
    
    if (correct) {
      setScore(prev => prev + 1);
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak > bestStreak) {
        setBestStreak(newStreak);
      }
    } else {
      setStreak(0);
    }
  };

  // Move to next question - loops forever
  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setupQuestion(questions, nextIndex, availableChars);
    } else {
      const reshuffled = shuffleArray(availableChars);
      setQuestions(reshuffled);
      setCurrentIndex(0);
      setupQuestion(reshuffled, 0, availableChars);
    }
  };

  // Toggle vowel selection
  const toggleVowel = (vowel: VowelType) => {
    setSelectedVowels(prev => 
      prev.includes(vowel) 
        ? prev.filter(v => v !== vowel)
        : [...prev, vowel]
    );
  };

  // Select all vowels
  const selectAllVowels = () => {
    setSelectedVowels([...vowelOptions]);
  };

  // Clear all vowels
  const clearAllVowels = () => {
    setSelectedVowels([]);
  };

  // Reset to menu
  const backToMenu = () => {
    setGameState('menu');
  };

  // Calculate accuracy percentage
  const getAccuracy = () => {
    if (answered === 0) return 0;
    return Math.round((score / answered) * 100);
  };

  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="quiz-container">
        <h1 className="quiz-title">かな Quiz</h1>
        <p className="quiz-subtitle">Test your Japanese kana knowledge</p>
        
        {/* Kana Type Selection */}
        <div className="settings-section">
          <h2 className="settings-title">Kana Type</h2>
          
          <div className="kana-mode-grid">
            <button
              className={`kana-mode-btn ${kanaMode === 'hiragana' ? 'active' : ''}`}
              onClick={() => setKanaMode('hiragana')}
            >
              <span className="kana-mode-char">あ</span>
              <span className="kana-mode-label">Hiragana</span>
            </button>
            <button
              className={`kana-mode-btn ${kanaMode === 'katakana' ? 'active' : ''}`}
              onClick={() => setKanaMode('katakana')}
            >
              <span className="kana-mode-char">ア</span>
              <span className="kana-mode-label">Katakana</span>
            </button>
            <button
              className={`kana-mode-btn ${kanaMode === 'both' ? 'active' : ''}`}
              onClick={() => setKanaMode('both')}
            >
              <span className="kana-mode-char">あア</span>
              <span className="kana-mode-label">Both</span>
            </button>
          </div>
        </div>
        
        {/* Vowel Row Selection */}
        <div className="settings-section">
          <h2 className="settings-title">Select Vowel Rows</h2>
          <p className="settings-hint">Practice specific vowel sounds</p>
          
          <div className="vowel-grid">
            {vowelOptions.map(vowel => (
              <button
                key={vowel}
                className={`vowel-btn ${selectedVowels.includes(vowel) ? 'active' : ''}`}
                onClick={() => toggleVowel(vowel)}
              >
                <span className="vowel-label">{vowel}</span>
                <span className="vowel-example">
                  {vowel === 'a' && (kanaMode === 'katakana' ? 'ア カ サ' : 'あ か さ')}
                  {vowel === 'i' && (kanaMode === 'katakana' ? 'イ キ シ' : 'い き し')}
                  {vowel === 'u' && (kanaMode === 'katakana' ? 'ウ ク ス' : 'う く す')}
                  {vowel === 'e' && (kanaMode === 'katakana' ? 'エ ケ セ' : 'え け せ')}
                  {vowel === 'o' && (kanaMode === 'katakana' ? 'オ コ ソ' : 'お こ そ')}
                  {vowel === 'n' && (kanaMode === 'katakana' ? 'ン' : 'ん')}
                </span>
              </button>
            ))}
          </div>
          
          <div className="quick-select">
            <button className="quick-btn" onClick={selectAllVowels}>Select All</button>
            <button className="quick-btn" onClick={clearAllVowels}>Clear All</button>
          </div>
        </div>
        
        {/* Extra Characters */}
        <div className="settings-section">
          <h2 className="settings-title">Extra Characters</h2>
          
          <div className="toggle-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={includeDakuten}
                onChange={(e) => setIncludeDakuten(e.target.checked)}
              />
              <span className="toggle-text">
                Dakuten {kanaMode === 'katakana' ? '(ガ ザ ダ バ)' : '(が ざ だ ば)'}
              </span>
            </label>
            
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={includeHandakuten}
                onChange={(e) => setIncludeHandakuten(e.target.checked)}
              />
              <span className="toggle-text">
                Handakuten {kanaMode === 'katakana' ? '(パ ピ プ)' : '(ぱ ぴ ぷ)'}
              </span>
            </label>
          </div>
        </div>
        
        <button className="start-btn" onClick={startQuiz}>
          Start Quiz
        </button>
        
        <p className="char-count">
          {getCharCount()} characters selected
        </p>
      </div>
    );
  }

  // Quiz Screen
  const currentQuestion = questions[currentIndex];

  return (
    <div className="quiz-container quiz-active">
      <div className="quiz-header">
        <button className="back-btn" onClick={backToMenu}>✕</button>
        <div className="stats-row">
          <span className="stat-item">{score} ✓</span>
          <span className="stat-item accuracy">{getAccuracy()}%</span>
          {streak >= 3 && <span className="stat-item streak">🔥 {streak}</span>}
        </div>
      </div>
      
      <div className="question-section">
        <p className="question-label">
          What is this {currentQuestion?.type === 'katakana' ? 'katakana' : 'hiragana'}?
        </p>
        <div className={`hiragana-display ${isCorrect === true ? 'correct' : ''} ${isCorrect === false ? 'wrong' : ''}`}>
          {currentQuestion?.character}
        </div>
      </div>
      
      <div className="options-grid">
        {options.map((option, index) => {
          let btnClass = 'option-btn';
          if (selectedAnswer !== null) {
            if (option === currentQuestion.romaji) {
              btnClass += ' correct';
            } else if (option === selectedAnswer) {
              btnClass += ' wrong';
            } else {
              btnClass += ' disabled';
            }
          }
          
          return (
            <button
              key={index}
              className={btnClass}
              onClick={() => handleAnswer(option)}
              disabled={selectedAnswer !== null}
            >
              {option}
            </button>
          );
        })}
      </div>
      
      {selectedAnswer !== null && (
        <div className="feedback-section">
          <p className={`feedback ${isCorrect ? 'correct' : 'wrong'}`}>
            {isCorrect ? '正解! Correct!' : `The answer is: ${currentQuestion.romaji}`}
          </p>
          <button className="next-btn" onClick={nextQuestion}>
            Next →
          </button>
        </div>
      )}
      
      <div className="question-counter">
        {answered} answered • Best streak: {bestStreak}
      </div>
    </div>
  );
};

export default HiraganaQuiz;
